// /home/labdoo/zitech-nexus/frontend/src/pages/admin/UserEnrollmentsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminTrainingAPI } from '../../services/api';
import './TrainingManagement.css';

interface Enrollment {
  id: number;
  user_email: string;
  user_name: string;
  technology: string;
  status: string;
  progress_percentage: number;
  service_name: string;
  started_at: string;
  completed_at: string;
  created_at: string;
}

const UserEnrollmentsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await adminTrainingAPI.getAllEnrollments();
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      await adminTrainingAPI.updateEnrollment(id, { status });
      fetchEnrollments();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#007bff';
      default: return '#ffc107';
    }
  };

  const filteredEnrollments = filter === 'all' 
    ? enrollments 
    : enrollments.filter(e => e.status === filter);

  if (loading) {
    return (
      <div className="training-management-loading">
        <div className="spinner"></div>
        <p>Loading enrollments...</p>
      </div>
    );
  }

  return (
    <div className="training-management-container">
      <div className="management-header">
        <h1>User Training Enrollments</h1>
      </div>

      <div className="filter-bar">
        <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All ({enrollments.length})
        </button>
        <button className={`filter-btn ${filter === 'not_started' ? 'active' : ''}`} onClick={() => setFilter('not_started')}>
          Not Started ({enrollments.filter(e => e.status === 'not_started').length})
        </button>
        <button className={`filter-btn ${filter === 'in_progress' ? 'active' : ''}`} onClick={() => setFilter('in_progress')}>
          In Progress ({enrollments.filter(e => e.status === 'in_progress').length})
        </button>
        <button className={`filter-btn ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
          Completed ({enrollments.filter(e => e.status === 'completed').length})
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Technology</th>
              <th>Service</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Started</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEnrollments.map((enrollment) => (
              <tr key={enrollment.id}>
                <td>{enrollment.user_name}<br/><small>{enrollment.user_email}</small></td>
                <td><strong>{enrollment.technology}</strong></td>
                <td>{enrollment.service_name}</td>
                <td>
                  <div className="progress-cell">
                    <div className="progress-bar-small">
                      <div className="progress-fill-small" style={{ width: `${enrollment.progress_percentage}%` }} />
                    </div>
                    <span>{enrollment.progress_percentage}%</span>
                  </div>
                </td>
                <td>
                  <select 
                    className="status-select"
                    style={{ backgroundColor: getStatusColor(enrollment.status), color: 'white' }}
                    value={enrollment.status}
                    onChange={(e) => updateStatus(enrollment.id, e.target.value)}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </td>
                <td>{enrollment.started_at ? new Date(enrollment.started_at).toLocaleDateString() : '-'}</td>
                <td>
                  <button 
                    className="action-btn view"
                    onClick={() => navigate(`/dashboard/admin/training/enrollments/${enrollment.id}`)}
                  >
                    View Progress
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserEnrollmentsList;